import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Subscription, filter, fromEvent, take } from 'rxjs';
import { DetailTablePanelContextMenuItem } from './models/detail-table-panel-context-menu-item';
import { DetailTablePanelContextMenuItemEvent } from './models/detail-table-panel-context-menu-item-event';

@Component({
    selector: 'app-detail-table-panel-context-menu',
    templateUrl: './detail-table-panel-context-menu.component.html',
    styleUrls: ['./detail-table-panel-context-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatMenuModule, FaIconComponent]
})
export class DetailTablePanelContextMenuComponent<T> implements OnDestroy {
    /**
     * Liste des éléments à afficher dans le menu contextuel.
     */
    @Input()
    contextMenuItems: Array<DetailTablePanelContextMenuItem<T>>;

    /**
     * Emis lorsque le menu contextuel se ferme.
     */
    @Output()
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    onClosed: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Emis lorsque l'utilisateur clique sur un élément du menu contextuel.
     */
    @Output()
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    onContextMenuItemClick: EventEmitter<DetailTablePanelContextMenuItemEvent<T>> = new EventEmitter<DetailTablePanelContextMenuItemEvent<T>>();

    /**
     * Reference au menu contextuel
     */
    @ViewChild(MatMenuTrigger)
    private contextMenu: MatMenuTrigger;

    /**
     * Position du menu contextuel.
     */
    public contextMenuPosition = { x: '0px', y: '0px' };

    /**
     * Souscription aux evenements Clic de souris en dehors du menu contextuel.
     */
    private clickOutside$: Subscription;

    /**
     * Retourne une reference à l'element natif associé au menu contextuel.
     */
    private get element(): any {
        return this.host.nativeElement;
    }

    /**
     * Constructeur.
     * @param host : Wrapper de l'element natif associé à ce composant.
     */
    constructor(private host: ElementRef) {
        this.SubscribeToMouseClickOutsideContextualMenu();
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.clickOutside$ && this.clickOutside$.unsubscribe();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Evenement émis par le clic de la souris.
     * @param menuItem : Element du menu selectionné.
     */
    public onContextMenuClick(event: MouseEvent, menuItem: DetailTablePanelContextMenuItem<T>): any {
        this.onContextMenuItemClick.emit(new DetailTablePanelContextMenuItemEvent(menuItem.menuItemId, menuItem.menuItemData));
    }

    /**
     * Ouvre le menu contextuel.
     * @param event : Evenement émis par le clic droit de la souris.
     */
    public openMenu(event: MouseEvent): void {
        this.contextMenuPosition.x = event.clientX - 5 + 'px';
        this.contextMenuPosition.y = event.clientY - 10 + 'px';
        this.contextMenu.openMenu();
    }

    /**
     * Ferme le menu contextuel.
     */
    private closeMenu(): void {
        this.clickOutside$ && this.clickOutside$.unsubscribe();
        this.onClosed.emit(true);
    }

    /**
     * S'abonne au clic souris en dehors du menu contextuel.
     */
    private SubscribeToMouseClickOutsideContextualMenu(): void {
        // Ferme le menu contextuel lorsque l'utilisateur clic en dehors du menu contextuel.
        this.clickOutside$ = fromEvent<MouseEvent>(document, 'click')
            .pipe(
                filter((event) => {
                    const clickTarget = event.target as HTMLElement;
                    return !!this.element && !this.element.contains(clickTarget);
                }),
                take(1)
            )
            .subscribe(() => this.closeMenu());
    }
}
